<template>
  <div class="users">
    <div class="page-header">
      <h1>{{ $t('admin.admin') }}</h1>
      <Button class="top-create--btn" type="primary" icon="md-add" @click="showCreateModal">{{
        $t('admin.createUser')
      }}</Button>
    </div>
    <Table :loading="loading" :columns="recordsCol" :data="records"></Table>
    <Page
      v-if="total >= 10"
      show-total
      :current="page"
      :total="total"
      :page-size="limit"
      @on-change="handlePageChange"
      @on-page-size-change="handlePageSizeChange"
    />
    <Modal
      v-model="createModalVisible"
      class-name="users-modal--create"
      :title="isEdit ? $t('admin.editUser') : $t('admin.createUser')"
      :width="480"
      :mask-closable="false"
    >
      <Form ref="createForm" :model="createForm" :rules="rules">
        <Row>
          <i-col span="24">
            <FormItem :label="$t('admin.role')" prop="roleId">
              <Select v-model="createForm.roleId" size="large">
                <Option v-for="item in rolesList" :key="item.value" :value="item.value" :label="item.label"> </Option>
              </Select>
            </FormItem>
          </i-col>
          <i-col span="24">
            <FormItem :label="$t('admin.username')" prop="username">
              <i-input v-model="createForm.username" size="large"></i-input>
            </FormItem>
          </i-col>
          <i-col span="24">
            <FormItem :label="$t('common.password')" prop="password">
              <i-input v-model="createForm.password" type="password" size="large"></i-input>
            </FormItem>
          </i-col>
          <i-col span="24">
            <FormItem :label="$t('common.confirmPassword')" type="password" prop="confirmPassword">
              <i-input v-model="createForm.confirmPassword" type="password" size="large"></i-input>
            </FormItem>
          </i-col>
        </Row>
      </Form>
      <div slot="footer">
        <Button type="text" @click="createModalVisible = false">{{ $t('common.cancel') }}</Button>
        <Button type="primary" @click="save">{{ $t('common.submit') }}</Button>
      </div>
    </Modal>
  </div>
</template>

<script lang="jsx">
import { loadUsers, loadRoles, createUser, deleteUser, viewUser, updateUser } from '@/api/admin'

export default {
  name: 'Users',

  data() {
    const validatePass = (rule, value, callback) => {
      if (value === '') {
        callback(new Error(this.$t('common.pleaseEnterYourPasswordAgain')))
      } else if (value !== this.createForm.password) {
        callback(new Error(this.$t('common.confirmNotMatch')))
      } else {
        callback()
      }
    }
    return {
      loading: false,
      createModalVisible: false,
      isEdit: false,
      page: 1,
      limit: 10,
      total: 0,
      createForm: {},
      rolesList: [],
      rules: {
        roleId: [{ required: true, message: this.$t('common.fillContent') }],
        username: [{ required: true, message: this.$t('common.fillContent') }],
        password: [{ required: true, message: this.$t('common.fillContent') }],
        confirmPassword: [
          { required: true, message: this.$t('common.pleaseEnterYourPasswordAgain') },
          { validator: validatePass, trigger: 'blur' },
        ],
      },
      records: [],
      recordsCol: [
        {
          title: this.$t('admin.username'),
          key: 'username',
        },
        {
          title: this.$t('admin.role'),
          key: 'roleId',
          render: (h, params) => {
            const roleName = this.findRoleName(params.row.roleId)
            return <div>{roleName}</div>
          },
        },
        {
          title: this.$t('common.operations'),
          key: 'action',
          width: 300,
          align: 'center',
          render: (h, params) => {
            const isAdmin = params.row.username === 'admin'
            const opers = (
              <div>
                <a
                  href="javascript:;"
                  style="margin-right: 10px;"
                  onClick={() => {
                    this.showDetail(params.row)
                  }}
                >
                  <Icon type="ios-create"></Icon>
                </a>
                <a href="javascript:;">
                  <Poptip
                    confirm={true}
                    transfer={true}
                    width={200}
                    title={this.$t('common.confirmDelete')}
                    placement="top"
                    type="error"
                    vOn:on-ok={() => {
                      this.remove(params.row)
                    }}
                    vOn:on-cancel={() => {}}
                  >
                    <Icon type="md-trash"></Icon>
                  </Poptip>
                </a>
              </div>
            )
            return isAdmin ? '' : opers
          },
        },
      ],
    }
  },

  watch: {
    createModalVisible(val) {
      if (!val) {
        this.$refs.createForm.resetFields()
        this.isEdit = false
        this.createForm = {}
      }
    },
  },

  created() {
    this.loadData()
  },

  methods: {
    loadData() {
      this.loading = true
      loadUsers({
        $limit: this.limit,
        $skip: (this.page - 1) * this.limit,
      })
        .then((res) => {
          this.records = res.data.data
          this.total = res.data.total
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
      loadRoles({
        $limit: 100,
      })
        .then((res) => {
          this.rolesList = res.data.data.map((item) => ({
            label: item.name,
            value: item.id,
          }))
        })
        .catch(() => {})
    },
    showDetail({ id }) {
      this.createModalVisible = true
      this.isEdit = true
      viewUser(id)
        .then((res) => {
          this.createForm = res.data
        })
        .catch(() => {})
    },
    remove({ id }) {
      deleteUser(id)
        .then(() => {
          this.$Message.success(this.$t('common.deleteSuccess'))
          this.page = 1
          this.loadData()
        })
        .catch(() => {})
    },
    showCreateModal() {
      this.createModalVisible = true
    },
    save() {
      this.$refs.createForm.validate((valid) => {
        if (!valid) {
          return
        }
        const { id, roleId, username, password } = this.createForm
        const data = { roleId, username, password }
        if (!this.isEdit) {
          createUser(data).then(() => {
            this.$Message.success(this.$t('common.addSuccess'))
            this.loadData()
            this.createModalVisible = false
          })
        } else {
          updateUser(id, data)
            .then(() => {
              this.$Message.success(this.$t('common.editSuccess'))
              this.loadData()
              this.createModalVisible = false
            })
            .catch(() => {})
        }
      })
    },
    findRoleName(roleId) {
      const currentRole = this.rolesList.find((role) => role.value === roleId)
      return currentRole ? currentRole.label : ''
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    handlePageSizeChange(val) {
      this.limit = val
      this.page = 1
      this.loadData()
    },
  },
}
</script>

<style lang="scss"></style>
